/**
 * Custom overrides for BilligKoste
 *
 *  - Force all Shopify money formatting to use Danish locale rules (dot for thousands, comma for decimals)
 *  - Re-apply formatting when theme JS updates price DOM nodes
 */

(function () {
  const PRICE_SELECTOR =
    ".price.price--highlight, .price.price--compare, .vat-price, .line-item__price.line-item__price--highlight, .line-item__price.line-item__price--compare, .cart-recap__price-line-price";
  const DATA_CENTS_ATTRIBUTE = "data-billigkoste-money-cents";

  const normaliseCents = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (typeof value === "number") {
      return Math.round(value);
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "") {
        return null;
      }

      // First try plain integer (common in data attributes)
      if (/^-?\d+$/.test(trimmed)) {
        return parseInt(trimmed, 10);
      }

      // Fall back to parsing textual prices such as "1.234,56 DKK"
      const normalised = trimmed
        .replace(/[^0-9,.-]/g, "") // keep digits, decimal separators and sign
        .replace(/\.(?=\d{3}(\D|$))/g, "") // strip thousand dots
        .replace(",", "."); // convert decimal comma to dot

      const parsedFloat = parseFloat(normalised);
      if (Number.isNaN(parsedFloat)) {
        return null;
      }

      return Math.round(parsedFloat * 100);
    }

    return null;
  };

  const formatCents = (cents) => {
    let intCents = normaliseCents(cents);

    if (intCents === null) {
      return "";
    }

    const isNegative = intCents < 0;
    intCents = Math.abs(intCents);

    const whole = Math.floor(intCents / 100);
    const fraction = intCents % 100;

    const integerPart = whole
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const decimalPart = fraction.toString().padStart(2, "0");

    return `${isNegative ? "-" : ""}kr. ${integerPart},${decimalPart}`;
  };

  const extractCentsFromNode = (node) => {
    const candidateAttributes = [
      "data-money",
      "data-money-raw",
      "data-price",
      "data-price-raw",
      "data-value",
      "data-amount",
      "data-total",
    ];

    for (const attribute of candidateAttributes) {
      if (node.hasAttribute(attribute)) {
        const attributeValue = node.getAttribute(attribute);
        const cents = normaliseCents(attributeValue);
        if (cents !== null) {
          return cents;
        }
      }
    }

    const datasetKeys = [
      "money",
      "moneyRaw",
      "price",
      "priceRaw",
      "value",
      "amount",
      "total",
    ];

    for (const key of datasetKeys) {
      if (Object.prototype.hasOwnProperty.call(node.dataset, key)) {
        const datasetValue = node.dataset[key];
        const cents = normaliseCents(datasetValue);
        if (cents !== null) {
          return cents;
        }
      }
    }

    return normaliseCents(node.textContent);
  };

  const applyFormattedMoney = (node) => {
    const cents = extractCentsFromNode(node);
    if (cents === null) {
      return;
    }

    node.setAttribute(DATA_CENTS_ATTRIBUTE, cents.toString());
    node.textContent = formatCents(cents);
  };

  const reformatPrices = (root = document) => {
    root.querySelectorAll(PRICE_SELECTOR).forEach((node) => applyFormattedMoney(node));
  };

  // App bundle (Avada) occasionally injects multiple tax labels.
  // Keep at most one helper per price node so the UI stays clean,
  // and exit early if markup is already fine (future-proof if the app is fixed).
  const normalizeTaxHelpers = (root = document) => {
    const priceBlocks = root.querySelectorAll(".Avada-Offer__Price");
    priceBlocks.forEach((priceBlock) => {
      const discountPrice = priceBlock.querySelector(".AOV-Offer__DiscountPrice");
      const defaultPrice = priceBlock.querySelector(".AOV-Offer__BasePrice");

      // Remove all existing helpers so we can rebuild a clean, single set.
      priceBlock.querySelectorAll(".tax-price-helper").forEach((el) => el.remove());

      // Recreate up to two helpers (one per price line) to keep UI consistent.
      if (discountPrice) {
        const helper = document.createElement("span");
        helper.className = "tax-price-helper";
        helper.textContent = "(Inkl. moms)";
        discountPrice.insertAdjacentElement("afterend", helper);
      }
      if (defaultPrice) {
        const helper = document.createElement("span");
        helper.className = "tax-price-helper";
        helper.textContent = "(Inkl. moms)";
        defaultPrice.insertAdjacentElement("afterend", helper);
      }
    });
  };

  // Low-latency helper normalization: run immediately, then coalesce a follow-up in rAF.
  const normalizeTaxHelpersThrottled = (() => {
    let scheduled = false;
    return (root = document) => {
      // run immediately to minimize visible lag
      normalizeTaxHelpers(root);
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        normalizeTaxHelpers(root);
      });
    };
  })();

  const applyCustomFormatting = (root = document) => {
    reformatPrices(root);
    normalizeTaxHelpers(root);
  };

  // Override Shopify.formatMoney so future theme invocations use our formatter.
  window.Shopify = window.Shopify || {};
  const originalFormatMoney = window.Shopify.formatMoney;

  window.Shopify.formatMoney = function (money, format) {
    const cents = normaliseCents(money);
    const formatted = formatCents(cents);
    return formatted || (originalFormatMoney ? originalFormatMoney(money, format) : "");
  };

  // Initial format after DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => applyCustomFormatting());
  } else {
    applyCustomFormatting();
  }

  // Observe DOM mutations to keep formatting in sync
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) {
          return;
        }

        if (node.matches && node.matches(PRICE_SELECTOR)) {
          applyFormattedMoney(node);
          normalizeTaxHelpers(node.parentElement || node);
        } else if (node.querySelector) {
          applyCustomFormatting(node);
        }
      });

      // Also sanitize the mutation target subtree in case the app rewrites innerHTML
      // without adding top-level nodes we explicitly catch above.
      if (mutation.target instanceof HTMLElement) {
        normalizeTaxHelpersThrottled(mutation.target);
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Re-apply formatting after known theme events
  const watchedEvents = [
    "product:added",
    "variant:changed",
    "cart:refresh",
    "cart:updated",
    "theme:loading:end",
    "shopify:section:load",
    "shopify:section:reorder",
    "shopify:section:select",
    "shopify:section:deselect",
    "shopify:block:select",
    "shopify:block:deselect",
  ];

  watchedEvents.forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (event && event.target instanceof HTMLElement) {
        applyCustomFormatting(event.target);
      } else {
        applyCustomFormatting();
      }
    });
  });

  // Also normalize after Avada’s own custom events if present.
  document.addEventListener("aov:bundle:updated", () => applyCustomFormatting());
  document.addEventListener("aov:bundle:selected", () => applyCustomFormatting());

  // Final safety: periodic cleanup in case app mutations slip past observers.
  setInterval(() => normalizeTaxHelpersThrottled(document), 200);
})();
